import { ComponentType, PropsWithChildren, ReactNode, useState } from "react"
import { useTranslation } from "react-i18next"
import * as Dialog from "@radix-ui/react-dialog"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import CloseIcon from "@/assets/icons/close/bold.svg?react"
import InfoBoldIcon from "@/assets/icons/info/bold.svg?react"
import InfoIcon from "@/assets/icons/info/medium.svg?react"
import { Button as DsButton } from "@/components/ds/Button"
import Spinner from "@/components/ui/Spinner.tsx"
import { cn } from "@/lib/utils.ts"

import { Button } from "./button"

export interface IModalProps {
  children: ReactNode
  title: ReactNode
  onClose: () => unknown
  onSave?: () => unknown
  isOpen: boolean
  className?: string
  size?: "sm" | "md" | "lg" | "full"
}

function Modal({ children, onClose, onSave, isOpen, title, className, size = "md" }: IModalProps) {
  const sizeClasses = {
    sm: "mobile-lg:w-[400px] sm:w-[500px]",
    md: "md:w-[550px]",
    lg: "xl:w-[1086px] desktop:w-[1086px] 2xl:w-[1200px]",
    full: "desktop-lg:w-[1400px] ultrawide:w-[1600px]",
  }
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async () => {
    if (!onSave) return

    const result = onSave()
    if (result instanceof Promise) {
      setIsSubmitting(true)
      result.then(onClose).finally(() => setIsSubmitting(false))
    }
  }
  return (
    <Dialog.Root open={isOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-0 z-50 h-screen w-[90%] -translate-x-1/2 transform overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2",
            sizeClasses[size],
            className,
          )}
          onInteractOutside={onClose}
          onClick={onClose}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={
              "absolute top-1/2 flex max-h-[90%] w-full -translate-y-1/2 transform flex-col overflow-hidden rounded-3xl bg-white data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]"
            }
          >
            {
              <div className="flex items-center justify-between border-b border-b-neutral-border p-6 pb-4">
                <Dialog.Title className="text-xl font-medium text-neutral-title">{title}</Dialog.Title>
                <DialogPrimitive.Close
                  onClick={onClose}
                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100"
                >
                  <CloseIcon className={"h-5 w-5"} />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              </div>
            }

            <div className="max-h-full w-full shrink overflow-auto p-6">{children}</div>

            {/* Footer with buttons */}
            {onSave && (
              <div className="flex justify-between border-t border-t-neutral-border p-5 shadow-[0px_-6px_16px_0px_rgba(6,15,51,0.04)]">
                <Button size="md" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                {isSubmitting ? (
                  <Spinner large className={"m-[9px]"} />
                ) : (
                  <Button size="md" variant="default" onClick={handleSave}>
                    Save
                  </Button>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

interface IModalInfo {
  isOpen: boolean
  setIsOpen: (val: boolean) => void
  Content: ComponentType
}

export const ModalInfo = ({ Content, isOpen, setIsOpen }: IModalInfo) => {
  const { t } = useTranslation()
  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={<div className={"flex items-center gap-2"}>{t("information")}</div>}
    >
      <Content />
    </Modal>
  )
}

export const ModalInfoButton = ({
  Content,
  children,
  className,
  size,
  iconOnly,
}: PropsWithChildren<Pick<IModalInfo, "Content"> & { className?: string; size?: "lg" | "md"; iconOnly?: boolean }>) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      {iconOnly ? (
        <button type="button" onClick={() => setIsOpen(true)} className={cn("cursor-pointer", className)}>
          {size === "lg" ? <InfoBoldIcon className="inline-block w-8" /> : <InfoIcon className="inline-block w-6" />}
        </button>
      ) : (
        <DsButton
          variant="outline"
          size="medium"
          className={cn("min-w-fit", className)}
          onClick={() => setIsOpen(true)}
        >
          {size === "lg" ? <InfoBoldIcon className="inline-block w-8" /> : <InfoIcon className="inline-block w-6" />}
          {children ?? "Learn more"}
        </DsButton>
      )}

      <ModalInfo isOpen={isOpen} setIsOpen={setIsOpen} Content={Content} />
    </>
  )
}

export default Modal
